import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, Layers, FolderOpen, Image } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearch, SearchResult } from "@/hooks/useSearch";

const typeIcons: Record<string, typeof Layers> = {
  category: Layers,
  subcategory: FolderOpen,
  project: Image,
};

const typeLabels: Record<string, string> = {
  category: "Categoria",
  subcategory: "Sottocategoria",
  project: "Lavoro",
};

const SearchBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const results = useSearch(query);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [query]);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handleSelect = (result: SearchResult) => {
    navigate(result.path);
    setIsOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && selectedIndex >= 0 && results[selectedIndex]) {
      handleSelect(results[selectedIndex]);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Search Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-foreground/60 hover:text-foreground transition-colors duration-300 p-1"
        aria-label="Cerca"
      >
        <Search size={18} />
      </button>

      {/* Search Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed left-3 right-3 top-[72px] md:absolute md:left-auto md:right-0 md:top-full md:mt-3 md:w-[420px] z-50"
          >
            <div className="bg-background border border-foreground/10 rounded-md shadow-2xl overflow-hidden">
              {/* Input */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-foreground/10">
                <Search size={16} className="text-foreground/40 flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Cerca servizi, categorie, lavori..."
                  className="flex-1 bg-transparent text-foreground font-body text-sm placeholder:text-foreground/30 focus:outline-none"
                />
                {query && (
                  <button
                    onClick={() => setQuery("")}
                    className="text-foreground/30 hover:text-foreground/60 transition-colors"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Results */}
              <div className="max-h-[320px] overflow-y-auto">
                {query.trim().length < 2 ? (
                  <div className="px-4 py-6 text-center">
                    <p className="text-foreground/30 font-body text-xs">
                      Digita almeno 2 caratteri per cercare
                    </p>
                  </div>
                ) : results.length === 0 ? (
                  <div className="px-4 py-6 text-center">
                    <p className="text-foreground/40 font-body text-sm">
                      Nessun risultato per "{query}"
                    </p>
                    <p className="text-foreground/25 font-body text-xs mt-1">
                      Prova con un termine diverso
                    </p>
                  </div>
                ) : (
                  <div className="py-1">
                    {results.map((result, i) => {
                      const Icon = typeIcons[result.type];
                      return (
                        <button
                          key={result.path + i}
                          onClick={() => handleSelect(result)}
                          onMouseEnter={() => setSelectedIndex(i)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-150 ${
                            i === selectedIndex
                              ? "bg-foreground/10"
                              : "hover:bg-foreground/5"
                          }`}
                        >
                          <Icon size={16} className="text-accent flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-heading text-sm text-foreground truncate">
                              {result.label}
                            </p>
                            <p className="text-foreground/30 font-body text-xs">
                              {typeLabels[result.type]}
                              {result.description && ` — ${result.description}`}
                            </p>
                          </div>
                          <ArrowRight size={14} className="text-foreground/20 flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchBar;
